import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface CrawlStatus {
  domain: string;
  requestedDomain?: string;
  canonicalDomain?: string;
  aliasApplied?: boolean;
  totalUrls: number;
  pagesCrawled: number;
  pagesFailed: number;
  pagesInQueue: number;
  pagesAnalyzed?: number;
  isComplete: boolean;
  progress: number; // percentage
  phase?: 'crawling' | 'analyzing' | 'complete' | 'idle';
  estimatedTimeRemaining?: number; // seconds (optional)
  crawlCost?: number;
}

export interface LocaleGroup {
  key: string;
  label: string;
  hreflangs: string[];
  urlCount: number;
  confidence: 'hreflang' | 'heuristic';
  exampleUrls: string[];
}

export interface StartCrawlOptions {
  limit?: number;
  priority?: 'low' | 'medium' | 'high';
  frequency?: 'daily' | 'weekly' | 'monthly' | 'manual';
  enableBrowserRendering?: boolean;
  selectedSitemaps?: string[];
  selectedLocales?: string[];
  minLastmodFrom?: string | null;
  perSitemapLimits?: Record<string, number>;
  perSitemapLimitMode?: 'recent' | 'oldest' | 'balanced';
  uploadedSitemaps?: Array<{ filename: string; content: string }>;
}

export interface SitemapTreeNode {
  sitemapUrl: string;
  type: 'sitemapindex' | 'urlset';
  urlCount: number;
  filteredUrlCount?: number;
  childSitemapCount: number;
  exampleUrls: string[];
  lastmod?: string;
  /** Dominant locale group key when leaf sitemaps are split by language. */
  dominantLocaleKey?: string;
  children?: SitemapTreeNode[];
}

export interface SitemapParseFailure {
  sitemapUrl: string;
  error: string;
}

export interface StartCrawlResponse {
  success: boolean;
  urlsAdded: number;
  tasksCreated: number;
  requestedDomain?: string;
  canonicalDomain?: string;
  aliasApplied?: boolean;
  message?: string;
}

export type ProcessingStatus = 'no_data' | 'not_started' | 'partial' | 'complete';

export interface LinksBackfillStatus {
  status: ProcessingStatus;
  totalPages: number;
  pagesWithLinks: number;
  lastFetchedAt: string | null;
}

export interface LinksClassifyStatus {
  status: ProcessingStatus;
  pagesWithLinks: number;
  pagesWithClassifiedLinks: number;
  lastClassifiedAt: string | null;
}

export interface TopicsMapStatus {
  status: ProcessingStatus;
  mappingCount: number;
  lastUpdatedAt: string | null;
  /**
   * Topics map is workspace-global. This is true when the last map run predates
   * this domain's crawl, i.e. it must be re-run to incorporate this domain.
   */
  staleForDomain?: boolean;
}

export interface TopicsAggregateStatus {
  status: ProcessingStatus;
  aggregateCount: number;
  pagesWithTopicSignals: number;
  lastUpdatedAt: string | null;
}

export interface CrawlEstimate {
  requestedDomain?: string;
  canonicalDomain?: string;
  aliasApplied?: boolean;
  sitemapUrl: string;
  discoveredSitemaps?: string[];
  sitemapTree?: SitemapTreeNode[];
  sitemapParseFailures?: SitemapParseFailure[];
  totalPagesInSitemap: number;
  duplicateUrlsInSitemaps?: number;
  parsedSitemaps?: number;
  sitemapParseTruncated?: boolean;
  filteredPages: number;
  selectedPagesInSitemap?: number;
  selectedFilteredPages?: number;
  minLastmodFrom?: string | null;
  localeGroups?: LocaleGroup[];
  localeDetectionConfidence?: 'hreflang' | 'heuristic' | null;
  localePartitionedSitemaps?: boolean;
  selectedLocales?: string[];
  limit: number;
  estimatedCostPerPage: number;
  estimatedTotalCost: number;
  selectedEstimatedTotalCost?: number;
  sitemapSource?: 'remote' | 'upload';
}

export interface FailedPage {
  url: string;
  errorMessage: string;
  taskId: string;
  createdAt: string;
  completedAt: string | null;
}

export interface EstEnrichmentStatus {
  status: ProcessingStatus;
  /** Analyzed pages (PageAnalysis count) — denominator for enrichment progress */
  totalPages: number;
  pagesWithTraffic?: number;
  pagesWithKeywords?: number;
  /** All tracked Url rows for the domain (may exceed analyzed pages on legacy crawls) */
  trackedUrls?: number;
  lastFetchedAt: string | null;
}

export interface InternalLinkSuggestionsStatus {
  status: ProcessingStatus | 'blocked';
  suggestionCount: number;
  lastGeneratedAt: string | null;
  blockedReason?: string | null;
  prerequisitesReady?: boolean;
  pagesWithClassifiedLinks?: number;
  pagesWithExtractedText?: number;
  pagesWithTopicSignals?: number;
}

export interface SupportingPagesBuildStatus {
  status: ProcessingStatus | 'blocked';
  snapshotCount: number;
  totalUrls: number;
  processedUrls: number;
  progressPercent: number;
  estimatedRemainingMs: number | null;
  estimatedRemainingLabel: string | null;
  lastGeneratedAt: string | null;
  startedAt: string | null;
  blockedReason?: string | null;
  prerequisitesReady?: boolean;
  /** True when the marked-competitor set changed since this snapshot was built. */
  staleForCompetitorSet?: boolean;
}

/** Readiness of a single marked competitor to contribute to supporting-pages builds. */
export interface CompetitorReadiness {
  domain: string;
  crawled: boolean;
  pagesAnalyzed: number;
  /** Pages that captured extractable body text. 0 with pagesAnalyzed > 0 ⇒ content not captured. */
  pagesWithContent?: number;
  hasTopicSignals: boolean;
  aggregateCount: number;
  ready: boolean;
  activeJob: 'queued' | 'running' | null;
}

export interface CompetitorStatus {
  focalDomain: string;
  competitors: CompetitorReadiness[];
  staleForCompetitorSet: boolean;
}

/** A processing job currently queued or running (for live checklist state). */
export type ProcessingJobType =
  | 'links-backfill'
  | 'links-classify'
  | 'topics-map'
  | 'topics-aggregate'
  | 'action-plan-internal-links'
  | 'supporting-pages-by-topic'
  | 'supporting-pages-opportunities';

export interface ActiveProcessingJob {
  type: ProcessingJobType;
  domain: string;
  status: 'queued' | 'running';
  startedAt: string | null;
  queuedAt: string | null;
}

export interface DomainProcessingStatus {
  domain: string;
  linksBackfill: LinksBackfillStatus;
  linksClassify: LinksClassifyStatus;
  topicsMap: TopicsMapStatus;
  topicsAggregate: TopicsAggregateStatus;
  estTraffic?: EstEnrichmentStatus;
  estKeywords?: EstEnrichmentStatus;
  internalLinkSuggestions?: InternalLinkSuggestionsStatus;
  supportingPagesByTopic?: SupportingPagesBuildStatus;
  supportingPagesOpportunities?: SupportingPagesBuildStatus;
  /** Alias domains whose DataForSEO data feeds into this domain's enrichment (e.g. pre-rebrand domains) */
  enrichmentAliasDomains?: string[];
  /** Jobs currently queued/running for this domain (plus workspace-global topics-map). */
  activeJobs?: ActiveProcessingJob[];
}

export interface DeleteDomainDataResponse {
  success: boolean;
  data: {
    domain: string;
    deleted: {
      pageAnalysis: number;
      tasks: number;
      urls: number;
      domainCrawls: number;
      domainTopicAggregates: number;
      rankedKeywords: number;
      competitorFinderRuns: number;
      relatedPagesCaches: number;
      pageComparisonExecutions: number;
      topicFilteredEntityDiscoveries: number;
      topicSharedDomainCoverages: number;
      topicSharedEntityLists: number;
      topicDomainCoverages: number;
      shareLinks: number;
      domainAliases?: number;
    };
  };
  message?: string;
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class CrawlService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  /**
   * Get list of all domains
   */
  getDomains(): Observable<{ success: boolean; data: string[] }> {
    return this.http.get<{ success: boolean; data: string[] }>(`${this.API_URL}/urls/domains`);
  }

  /**
   * Start a crawl for a domain
   */
  startCrawl(domain: string, options: StartCrawlOptions = {}): Observable<StartCrawlResponse> {
    return this.http.post<StartCrawlResponse>(`${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl/start`, {
      limit: options.limit || 750,
      priority: options.priority || 'high',
      frequency: options.frequency || 'manual',
      enableBrowserRendering: options.enableBrowserRendering || false,
      selectedSitemaps: options.selectedSitemaps || [],
      selectedLocales: options.selectedLocales || [],
      minLastmodFrom: options.minLastmodFrom || null,
      perSitemapLimits: options.perSitemapLimits || {},
      perSitemapLimitMode: options.perSitemapLimitMode || 'recent',
      uploadedSitemaps: options.uploadedSitemaps || []
    });
  }

  private buildEstimateQueryParams(
    options: {
      limit?: number;
      selectedSitemaps?: string[];
      selectedLocales?: string[];
      minLastmodFrom?: string | null;
      perSitemapLimits?: Record<string, number>;
      perSitemapLimitMode?: 'recent' | 'oldest' | 'balanced';
    } = {}
  ): string {
    const search = new URLSearchParams();
    if (options.limit) search.set('limit', String(options.limit));
    if ((options.selectedSitemaps || []).length > 0) {
      search.set('selectedSitemaps', (options.selectedSitemaps || []).join(','));
    }
    if ((options.selectedLocales || []).length > 0) {
      search.set('selectedLocales', (options.selectedLocales || []).join(','));
    }
    if (options.minLastmodFrom) {
      search.set('minLastmodFrom', options.minLastmodFrom);
    }
    if (options.perSitemapLimits && Object.keys(options.perSitemapLimits).length > 0) {
      search.set('perSitemapLimits', JSON.stringify(options.perSitemapLimits));
    }
    if (options.perSitemapLimitMode) {
      search.set('perSitemapLimitMode', options.perSitemapLimitMode);
    }
    return search.toString();
  }

  /**
   * Estimate crawl cost for a domain (parses sitemap, zero API cost)
   */
  estimateCrawl(
    domain: string,
    options: {
      limit?: number;
      selectedSitemaps?: string[];
      selectedLocales?: string[];
      minLastmodFrom?: string | null;
      perSitemapLimits?: Record<string, number>;
      perSitemapLimitMode?: 'recent' | 'oldest' | 'balanced';
    } = {}
  ): Observable<{ success: boolean; data: CrawlEstimate }> {
    const params = this.buildEstimateQueryParams(options);
    return this.http.get<{ success: boolean; data: CrawlEstimate }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl/estimate${params ? `?${params}` : ''}`
    );
  }

  /**
   * Estimate crawl cost from operator-uploaded sitemap XML files (bot-protected sites).
   */
  estimateCrawlFromUpload(
    domain: string,
    files: File[],
    options: {
      limit?: number;
      selectedSitemaps?: string[];
      selectedLocales?: string[];
      minLastmodFrom?: string | null;
      perSitemapLimits?: Record<string, number>;
      perSitemapLimitMode?: 'recent' | 'oldest' | 'balanced';
    } = {}
  ): Observable<{ success: boolean; data: CrawlEstimate }> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('sitemaps', file, file.name);
    }
    const params = this.buildEstimateQueryParams(options);
    return this.http.post<{ success: boolean; data: CrawlEstimate }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl/estimate-upload${params ? `?${params}` : ''}`,
      formData
    );
  }

  /**
   * Get failed crawl pages for a domain
   */
  getFailedPages(domain: string): Observable<FailedPage[]> {
    return this.http.get<{ success: boolean; data: FailedPage[] }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl/failed`
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error('Failed to get failed pages');
      })
    );
  }

  /**
   * Get crawl status for a domain
   */
  getCrawlStatus(domain: string): Observable<CrawlStatus> {
    return this.http.get<{ success: boolean; data: CrawlStatus }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl/status`
    ).pipe(
      // Extract data from response
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        } else {
          throw new Error('Failed to get crawl status');
        }
      })
    );
  }

  /**
   * Get processing status for a domain (links/topics workflows)
   */
  getProcessingStatus(domain: string): Observable<DomainProcessingStatus> {
    return this.http.get<{ success: boolean; data: DomainProcessingStatus }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/processing-status`
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        } else {
          throw new Error('Failed to get processing status');
        }
      })
    );
  }

  /**
   * Trigger a processing step for a domain
   */
  runProcessingStep(domain: string, step: 'links-backfill' | 'links-classify' | 'topics-map' | 'topics-aggregate'): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/${step}`, {}
    );
  }

  /**
   * Run all processing steps sequentially for a domain
   */
  runAllProcessing(
    domain: string,
    topicsMapDomains?: string[]
  ): Observable<{ success: boolean; message: string }> {
    const body = topicsMapDomains && topicsMapDomains.length ? { topicsMapDomains } : {};
    return this.http.post<{ success: boolean; message: string }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/run-all`, body
    );
  }

  /** Run a single processing endpoint by its `process/<endpoint>` suffix. */
  runProcessEndpoint(domain: string, endpoint: string): Observable<{ success: boolean; message?: string; data?: any }> {
    return this.http.post<{ success: boolean; message?: string; data?: any }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/${endpoint}`, {}
    );
  }

  /** Build supporting pages by topic (cheap topic-diagnostic snapshot). */
  runSupportingPagesByTopic(domain: string) {
    return this.runProcessEndpoint(domain, 'supporting-pages-by-topic');
  }

  /** Build supporting page opportunities (content-gap sidecar; opt-in, slow). */
  runSupportingPagesOpportunities(domain: string) {
    return this.runProcessEndpoint(domain, 'supporting-pages-opportunities');
  }

  /** Refresh estimated organic traffic (Labs). */
  refreshTraffic(domain: string) {
    return this.runProcessEndpoint(domain, 'traffic-enrichment');
  }

  /** Refresh ranked keywords domain dump (Labs cost; opt-in). */
  refreshKeywords(domain: string) {
    return this.runProcessEndpoint(domain, 'ranked-keywords-enrichment');
  }

  /** Rebuild internal-link suggestions. */
  refreshInternalLinks(domain: string) {
    return this.runProcessEndpoint(domain, 'internal-link-suggestions');
  }

  /** Run minimal topic prep (topics-map + topics-aggregate) for one competitor domain. */
  runCompetitorPrep(competitorDomain: string) {
    return this.runProcessEndpoint(competitorDomain, 'competitor-prep');
  }

  /** Run topic prep for all of the focal domain's ready (crawled) competitors at once. */
  runCompetitorPrepAll(focalDomain: string): Observable<{ success: boolean; message?: string; data?: { jobIds: string[]; readyDomains: string[] } }> {
    return this.http.post<{ success: boolean; message?: string; data?: { jobIds: string[]; readyDomains: string[] } }>(
      `${this.API_URL}/domains/${encodeURIComponent(focalDomain)}/competitors/prep-all`, {}
    );
  }

  /** Per-competitor readiness + supporting-pages staleness for the focal domain. */
  getCompetitorStatus(focalDomain: string): Observable<{ success: boolean; data: CompetitorStatus }> {
    return this.http.get<{ success: boolean; data: CompetitorStatus }>(
      `${this.API_URL}/domains/${encodeURIComponent(focalDomain)}/competitors/status`
    );
  }

  /**
   * Delete all known domain-related data (destructive)
   */
  deleteDomainData(domain: string, confirmDomain: string): Observable<DeleteDomainDataResponse> {
    return this.http.post<DeleteDomainDataResponse>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/delete-data`,
      { confirmDomain }
    );
  }
}

